import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Chip,
} from "@mui/material";

import RefreshIcon from "@mui/icons-material/Refresh";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SearchIcon from "@mui/icons-material/Search";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import { decipherEncryption } from "../../../helper/index";
import CertificateService from "../../../services/certificateServices";


// =====================================================
// CONSTANTS
// =====================================================

const STALLED_STATUSES = [
  "pending",
  "assigned",
  "paused",
];

const RESET_ROLES = [
  "superadmin",
  "certificateadmin",
];


// =====================================================
// HELPERS
// =====================================================

const getErrorMessage = (error) => {
  return (
    error?.response?.data?.error ||
    error?.response?.data?.detail ||
    error?.message ||
    "Something went wrong. Please try again."
  );
};


const formatDateTime = (value) => {
  if (!value) {
    return "—";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat("en-IN", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
};


const getStatusColor = (status) => {
  switch (String(status || "").toLowerCase()) {
    case "paused":
      return "warning";

    case "assigned":
      return "info";

    case "pending":
      return "error";

    default:
      return "default";
  }
};


// =====================================================
// GET USER ROLE
// =====================================================

const getUserRole = () => {
  try {
    const myDecipher = decipherEncryption("skytrack");

    const userData =
      sessionStorage.getItem("cookiesData") ||
      localStorage.getItem("cookiesData");

    if (!userData) {
      return "";
    }

    const data = userData
      .split("-")
      .map((item) => myDecipher(item));

    return String(data?.[1] || "")
      .toLowerCase()
      .trim();

  } catch (error) {
    console.error("Unable to read user role:", error);
    return "";
  }
};


// =====================================================
// MAIN COMPONENT
// =====================================================

export default function InstalledCertificateDevices() {

  // ---------------------------------------------------
  // DATA
  // ---------------------------------------------------

  const [rows, setRows] = useState([]);

  const [count, setCount] = useState(0);

  const [serverMinDays, setServerMinDays] = useState(2);


  // ---------------------------------------------------
  // FILTERS
  // ---------------------------------------------------

  const [minDays, setMinDays] = useState(2);

  const [search, setSearch] = useState("");

  const [statusFilter, setStatusFilter] = useState("all");


  // ---------------------------------------------------
  // PAGINATION
  // ---------------------------------------------------

  const [page, setPage] = useState(0);

  const [rowsPerPage, setRowsPerPage] = useState(10);


  // ---------------------------------------------------
  // LOADING / MESSAGES
  // ---------------------------------------------------

  const [loading, setLoading] = useState(false);

  const [resetting, setResetting] = useState(false);

  const [error, setError] = useState("");

  const [success, setSuccess] = useState("");


  // ---------------------------------------------------
  // RESET DIALOG
  // ---------------------------------------------------

  const [resetDialog, setResetDialog] = useState({
    open: false,
    job: null,
  });

  const [resetReason, setResetReason] = useState("");


  // ---------------------------------------------------
  // ROLE
  // ---------------------------------------------------

  const userRole = getUserRole();

  const canReset = RESET_ROLES.includes(userRole);


  // ===================================================
  // LOAD INSTALLED JOBS
  // ===================================================

  const loadStalledJobs = useCallback(async () => {

    setLoading(true);

    setError("");

    setSuccess("");

    try {

      const response =
        await CertificateService.getInstalledCertificateJobs({
          min_days: minDays,
        });


      const data = response?.data || {};


      const results = Array.isArray(data)
        ? data
        : data.results || [];


      setRows(results);

      setCount(
        Number(
          data.count ??
          results.length
        )
      );


      setServerMinDays(
        Number(
          data.min_days ??
          minDays
        )
      );


      setPage(0);

    } catch (err) {

      setRows([]);

      setCount(0);

      setError(
        getErrorMessage(err)
      );

    } finally {

      setLoading(false);

    }

  }, [minDays]);


  // ===================================================
  // INITIAL LOAD
  // ===================================================

  useEffect(() => {

    loadStalledJobs();

  }, [loadStalledJobs]);


  // ===================================================
  // SEARCH + STATUS FILTER
  // ===================================================

  const filteredRows = useMemo(() => {

    const query =
      search
        .trim()
        .toLowerCase();


    return rows.filter((row) => {

      const matchesStatus =
        statusFilter === "all" ||
        String(row.status || "")
          .toLowerCase() === statusFilter;


      if (!matchesStatus) {
        return false;
      }


      if (!query) {
        return true;
      }


      return [
        row.vehicle_reg_no,
        row.chassis_no,
        row.imei,
        row.protocol_format,
        row.state_name,
        row.district_name,
        row.manufacturer_name,
        row.assigned_agent_name,
        row.id,
        row.device_tag,
      ].some((value) =>
        String(value ?? "")
          .toLowerCase()
          .includes(query)
      );

    });

  }, [
    rows,
    search,
    statusFilter,
  ]);


  // ===================================================
  // PAGINATION
  // ===================================================

  const paginatedRows = useMemo(() => {

    return filteredRows.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );

  }, [
    filteredRows,
    page,
    rowsPerPage,
  ]);


  // ===================================================
  // STATISTICS
  // ===================================================

  const stats = useMemo(() => {

    const pending =
      rows.filter(
        (r) =>
          String(r.status).toLowerCase() ===
          "pending"
      ).length;


    const assigned =
      rows.filter(
        (r) =>
          String(r.status).toLowerCase() ===
          "assigned"
      ).length;


    const paused =
      rows.filter(
        (r) =>
          String(r.status).toLowerCase() ===
          "paused"
      ).length;


    return {
      pending,
      assigned,
      paused,
    };

  }, [rows]);


  // ===================================================
  // MIN DAYS CHANGE
  // ===================================================

  const handleMinDaysChange = (event) => {

    const value = event.target.value;


    if (value === "") {

      setMinDays("");

      return;

    }


    const parsed =
      Number(value);


    if (
      Number.isInteger(parsed) &&
      parsed >= 0
    ) {

      setMinDays(parsed);

    }

  };


  // ===================================================
  // APPLY MIN DAYS
  // ===================================================

  const applyMinDays = () => {

    const parsed =
      Number(minDays);


    if (
      !Number.isInteger(parsed) ||
      parsed < 0
    ) {

      setError(
        "min_days must be a non-negative integer."
      );

      return;

    }


    setMinDays(parsed);

  };


  // ===================================================
  // OPEN RESET DIALOG
  // ===================================================

  const openResetDialog = (job) => {

    setResetReason("");

    setResetDialog({
      open: true,
      job,
    });

  };


  // ===================================================
  // CLOSE RESET DIALOG
  // ===================================================

  const closeResetDialog = () => {

    if (resetting) {
      return;
    }


    setResetDialog({
      open: false,
      job: null,
    });


    setResetReason("");

  };


  // ===================================================
  // RESET STALLED JOB
  // ===================================================

  const handleReset = async () => {

    const job =
      resetDialog.job;


    const reason =
      resetReason.trim();


    if (!job) {
      return;
    }


    if (!reason) {

      setError(
        "Reset reason is required."
      );

      return;

    }


    if (
      !STALLED_STATUSES.includes(
        String(job.status).toLowerCase()
      )
    ) {

      setError(
        `Reset is only allowed for pending, assigned, or paused jobs. Current status: ${job.status}.`
      );

      closeResetDialog();

      return;

    }


    setResetting(true);

    setError("");

    setSuccess("");


    try {

      const response =
        await CertificateService
          .resetInstalledCertificateJob({
            job_id: job.id,
            reason,
          });


      const message =
        response?.data?.message ||
        "Installed device reset successfully.";


      setSuccess(
        `${message} Job #${job.id} ${
          job.vehicle_reg_no ||
          job.imei ||
          "device"
        }`
      );


      closeResetDialog();


      await loadStalledJobs();

    } catch (err) {

      setError(
        getErrorMessage(err)
      );

    } finally {

      setResetting(false);

    }

  };


  // ===================================================
  // UI
  // ===================================================

  return (

    <Box
      sx={{
        p: {
          xs: 2,
          md: 3,
        },
        bgcolor: "#f7f8fa",
        minHeight: "100vh",
      }}
    >

      <Stack spacing={2.5}>


        {/* ===========================================
            HEADER
        ============================================ */}

        <Stack
          direction={{
            xs: "column",
            md: "row",
          }}
          alignItems={{
            xs: "flex-start",
            md: "center",
          }}
          justifyContent="space-between"
          spacing={2}
        >

          <Box>

            <Typography
              variant="h5"
              fontWeight={700}
            >
              Incomplete Device
            </Typography>


            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.5 }}
            >
              Devices where commercial activation,
              tagging, and technician installation are
              complete, but the certificate job is still
              not submitted or approved.
            </Typography>

          </Box>


          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadStalledJobs}
            disabled={loading}
          >
            Refresh
          </Button>

        </Stack>


        {/* ===========================================
            ERROR
        ============================================ */}

        {error && (

          <Alert
            severity="error"
            onClose={() => setError("")}
          >
            {error}
          </Alert>

        )}


        {/* ===========================================
            SUCCESS
        ============================================ */}

        {success && (

          <Alert
            severity="success"
            onClose={() => setSuccess("")}
          >
            {success}
          </Alert>

        )}


        {/* ===========================================
            STAT CARDS
        ============================================ */}

        <Stack
          direction={{
            xs: "column",
            sm: "row",
          }}
          spacing={2}
        >

          {/* TOTAL */}

          <Card sx={{ flex: 1 }}>

            <CardContent>

              <Stack
                direction="row"
                spacing={1.5}
                alignItems="center"
              >

                <WarningAmberIcon
                  color="error"
                />

                <Box>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                  >
                    Total Installed
                  </Typography>

                  <Typography
                    variant="h5"
                    fontWeight={700}
                  >
                    {count}
                  </Typography>

                </Box>

              </Stack>

            </CardContent>

          </Card>


          {/* PENDING */}

          <Card sx={{ flex: 1 }}>

            <CardContent>

              <Stack
                direction="row"
                spacing={1.5}
                alignItems="center"
              >

                <PendingActionsIcon
                  color="error"
                />

                <Box>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                  >
                    Pending
                  </Typography>

                  <Typography
                    variant="h5"
                    fontWeight={700}
                  >
                    {stats.pending}
                  </Typography>

                </Box>

              </Stack>

            </CardContent>

          </Card>


          {/* ASSIGNED */}

          <Card sx={{ flex: 1 }}>

            <CardContent>

              <Stack
                direction="row"
                spacing={1.5}
                alignItems="center"
              >

                <CheckCircleOutlineIcon
                  color="info"
                />

                <Box>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                  >
                    Assigned
                  </Typography>

                  <Typography
                    variant="h5"
                    fontWeight={700}
                  >
                    {stats.assigned}
                  </Typography>

                </Box>

              </Stack>

            </CardContent>

          </Card>


          {/* PAUSED */}

          <Card sx={{ flex: 1 }}>

            <CardContent>

              <Stack
                direction="row"
                spacing={1.5}
                alignItems="center"
              >

                <PendingActionsIcon
                  color="warning"
                />

                <Box>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                  >
                    Paused
                  </Typography>

                  <Typography
                    variant="h5"
                    fontWeight={700}
                  >
                    {stats.paused}
                  </Typography>

                </Box>

              </Stack>

            </CardContent>

          </Card>

        </Stack>


        {/* ===========================================
            FILTERS
        ============================================ */}

        <Paper sx={{ p: 2 }}>

          <Stack
            direction={{
              xs: "column",
              md: "row",
            }}
            spacing={1.5}
            alignItems={{
              xs: "stretch",
              md: "center",
            }}
          >

            {/* MIN DAYS */}

            <TextField
              label="Minimum Installed days"
              type="number"
              size="small"
              value={minDays}
              onChange={handleMinDaysChange}
              onKeyDown={(event) => {

                if (event.key === "Enter") {
                  applyMinDays();
                }

              }}
              inputProps={{
                min: 0,
                step: 1,
              }}
              sx={{
                width: {
                  xs: "100%",
                  md: 190,
                },
              }}
            />


            <Button
              variant="contained"
              onClick={applyMinDays}
              disabled={loading}
            >
              Apply
            </Button>


            <Divider
              orientation="vertical"
              flexItem
              sx={{
                display: {
                  xs: "none",
                  md: "block",
                },
              }}
            />


            {/* SEARCH */}

            <TextField
              fullWidth
              size="small"
              label="Search"
              placeholder="Vehicle, IMEI, chassis, district, agent..."
              value={search}
              onChange={(event) => {

                setSearch(
                  event.target.value
                );

                setPage(0);

              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon
                      fontSize="small"
                    />
                  </InputAdornment>
                ),
              }}
            />


            {/* STATUS */}

            <TextField
              select
              size="small"
              label="Status"
              value={statusFilter}
              onChange={(event) => {

                setStatusFilter(
                  event.target.value
                );

                setPage(0);

              }}
              sx={{
                minWidth: 140,
              }}
            >

              <MenuItem value="all">
                All
              </MenuItem>

              <MenuItem value="pending">
                Pending
              </MenuItem>

              <MenuItem value="assigned">
                Assigned
              </MenuItem>

              <MenuItem value="paused">
                Paused
              </MenuItem>

            </TextField>

          </Stack>


          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            sx={{ mt: 1 }}
          >
            Server threshold: {serverMinDays} day(s)
          </Typography>

        </Paper>


        {/* ===========================================
            TABLE
        ============================================ */}

        <Paper sx={{ overflow: "hidden" }}>

          <TableContainer
            sx={{
              maxHeight:
                "calc(100vh - 390px)",
            }}
          >

            <Table
              stickyHeader
              size="small"
            >

              <TableHead>

                <TableRow>

                  <TableCell
                    sx={{ fontWeight: 700 }}
                  >
                    Job
                  </TableCell>

                  <TableCell
                    sx={{ fontWeight: 700 }}
                  >
                    Vehicle
                  </TableCell>

                  <TableCell
                    sx={{ fontWeight: 700 }}
                  >
                    IMEI
                  </TableCell>

                  <TableCell
                    sx={{ fontWeight: 700 }}
                  >
                    Location
                  </TableCell>

                  <TableCell
                    sx={{ fontWeight: 700 }}
                  >
                    Protocol
                  </TableCell>

                  <TableCell
                    sx={{ fontWeight: 700 }}
                  >
                    Agent
                  </TableCell>

                  <TableCell
                    sx={{ fontWeight: 700 }}
                  >
                    Status
                  </TableCell>

                  <TableCell
                    sx={{ fontWeight: 700 }}
                  >
                    Installed Since
                  </TableCell>

                  <TableCell
                    sx={{ fontWeight: 700 }}
                  >
                    Days
                  </TableCell>

                  <TableCell
                    align="right"
                    sx={{ fontWeight: 700 }}
                  >
                    Action
                  </TableCell>

                </TableRow>

              </TableHead>


              <TableBody>

                {/* LOADING */}

                {loading ? (

                  <TableRow>

                    <TableCell
                      colSpan={10}
                      align="center"
                      sx={{ py: 6 }}
                    >

                      <CircularProgress
                        size={30}
                      />

                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 1 }}
                      >
                        Loading Installed devices...
                      </Typography>

                    </TableCell>

                  </TableRow>

                ) : paginatedRows.length === 0 ? (

                  /* EMPTY */

                  <TableRow>

                    <TableCell
                      colSpan={10}
                      align="center"
                      sx={{ py: 6 }}
                    >

                      <Typography
                        fontWeight={600}
                      >
                        No Installed devices found
                      </Typography>

                      <Typography
                        variant="body2"
                        color="text.secondary"
                      >
                        Try changing the minimum days
                        or search filters.
                      </Typography>

                    </TableCell>

                  </TableRow>

                ) : (

                  /* DATA */

                  paginatedRows.map((row) => (

                    <TableRow
                      hover
                      key={row.id}
                    >

                      {/* JOB */}

                      <TableCell>

                        <Typography
                          fontWeight={600}
                        >
                          #{row.id}
                        </Typography>

                        <Typography
                          variant="caption"
                          color="text.secondary"
                        >
                          Tag #{row.device_tag ?? "—"}
                        </Typography>

                      </TableCell>


                      {/* VEHICLE */}

                      <TableCell>

                        <Typography
                          fontWeight={600}
                        >
                          {row.vehicle_reg_no || "—"}
                        </Typography>

                        <Typography
                          variant="caption"
                          color="text.secondary"
                        >
                          {row.chassis_no || "—"}
                        </Typography>

                      </TableCell>


                      {/* IMEI */}

                      <TableCell>
                        {row.imei || "—"}
                      </TableCell>


                      {/* LOCATION */}

                      <TableCell>

                        <Typography>
                          {row.state_name || "—"}
                        </Typography>

                        <Typography
                          variant="caption"
                          color="text.secondary"
                        >
                          {row.district_name || "—"}
                        </Typography>

                      </TableCell>


                      {/* PROTOCOL */}

                      <TableCell>

                        <Chip
                          size="small"
                          label={
                            row.protocol_format ||
                            "—"
                          }
                          variant="outlined"
                        />

                      </TableCell>


                      {/* AGENT */}

                      <TableCell>

                        {row.assigned_agent_name ||
                          "Unassigned"}

                      </TableCell>


                      {/* STATUS */}

                      <TableCell>

                        <Chip
                          size="small"
                          label={
                            row.status || "—"
                          }
                          color={getStatusColor(
                            row.status
                          )}
                        />

                      </TableCell>


                      {/* STALLED SINCE */}

                      <TableCell>

                        <Typography
                          variant="body2"
                        >
                          {formatDateTime(
                            row.stalled_since
                          )}
                        </Typography>

                      </TableCell>


                      {/* DAYS */}

                      <TableCell>

                        <Chip
                          size="small"
                          label={`${
                            row.days_stalled ?? 0
                          } day${
                            Number(
                              row.days_stalled
                            ) === 1
                              ? ""
                              : "s"
                          }`}
                          color={
                            Number(
                              row.days_stalled
                            ) >= 7
                              ? "error"
                              : "warning"
                          }
                          variant="outlined"
                        />

                      </TableCell>


                      {/* ACTION */}

                      <TableCell align="right">

                        {canReset ? (

                          <Tooltip
                            title="Reset installed device"
                          >

                            <IconButton
                              color="error"
                              onClick={() =>
                                openResetDialog(
                                  row
                                )
                              }
                              disabled={resetting}
                            >

                              <RestartAltIcon />

                            </IconButton>

                          </Tooltip>

                        ) : (

                          <Tooltip
                            title="Reset requires superadmin or certificateadmin"
                          >

                            <span>

                              <IconButton
                                disabled
                              >

                                <RestartAltIcon />

                              </IconButton>

                            </span>

                          </Tooltip>

                        )}

                      </TableCell>

                    </TableRow>

                  ))

                )}

              </TableBody>

            </Table>

          </TableContainer>


          {/* PAGINATION */}

          <TablePagination
            component="div"
            count={filteredRows.length}
            page={page}
            onPageChange={(_, nextPage) =>
              setPage(nextPage)
            }
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {

              setRowsPerPage(
                Number(event.target.value)
              );

              setPage(0);

            }}
            rowsPerPageOptions={[
              10,
              25,
              50,
              100,
            ]}
          />

        </Paper>

      </Stack>


      {/* =================================================
          RESET DIALOG
      ================================================== */}

      <Dialog
        open={resetDialog.open}
        onClose={closeResetDialog}
        fullWidth
        maxWidth="sm"
      >

        <DialogTitle>
          Reset Installed Device?
        </DialogTitle>


        <DialogContent>

          <Alert
            severity="warning"
            sx={{ mb: 2 }}
          >
            This action is irreversible. It will undo
            the applicable installation, tagging,
            commercial activation, certificate job,
            and uploaded certificate files for this
            device.
          </Alert>


          {resetDialog.job && (

            <Stack
              spacing={1.5}
              sx={{ mb: 2 }}
            >

              <Typography>
                <strong>Job:</strong>{" "}
                #{resetDialog.job.id}
              </Typography>

              <Typography>
                <strong>Vehicle:</strong>{" "}
                {resetDialog.job.vehicle_reg_no ||
                  "—"}
              </Typography>

              <Typography>
                <strong>IMEI:</strong>{" "}
                {resetDialog.job.imei || "—"}
              </Typography>

              <Typography>
                <strong>Current status:</strong>{" "}
                {resetDialog.job.status || "—"}
              </Typography>

            </Stack>

          )}


          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            label="Reset reason"
            placeholder="e.g. Device swapped in the field, redoing tag + install from scratch"
            value={resetReason}
            onChange={(event) =>
              setResetReason(
                event.target.value
              )
            }
            required
          />

        </DialogContent>


        <DialogActions
          sx={{
            px: 3,
            pb: 2,
          }}
        >

          <Button
            onClick={closeResetDialog}
            disabled={resetting}
          >
            Cancel
          </Button>


          <Button
            variant="contained"
            color="error"
            startIcon={
              resetting ? (
                <CircularProgress
                  size={18}
                  color="inherit"
                />
              ) : (
                <RestartAltIcon />
              )
            }
            onClick={handleReset}
            disabled={
              resetting ||
              !resetReason.trim()
            }
          >
            {resetting
              ? "Resetting..."
              : "Reset Device"}
          </Button>

        </DialogActions>

      </Dialog>

    </Box>

  );
}